import { FC, ReactNode } from 'react';
import { Col, Dropdown, Menu, Row } from 'antd';
import { TbLayoutGrid, TbLock, TbUsers, TbUsersGroup } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';

interface ResourceVisibilityDropdownProps {
    disabled?: boolean;
    options?: string[];
    visibility: string;
    setVisibility: (visibility: string) => void;
    children?: ReactNode;
}

const visibilityOptions = {
    Private: {
        description: 'Visible only to you and people you explicitly invite.',
        icon: TbLock,
    },
    Shared: {
        description: 'Visible to the people who already have access.',
        icon: TbUsers,
    },
    Team: {
        description: 'Visible to members of a selected team.',
        icon: TbUsersGroup,
    },
    Workspace: {
        description: 'Visible to everyone in the workspace.',
        icon: TbLayoutGrid,
    },
} as const;

const ResourceVisibilityDropdown: FC<ResourceVisibilityDropdownProps> = ({
    disabled = false,
    options = ['Private', 'Workspace'],
    visibility,
    setVisibility,
    children,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const VisibilityIcon = visibilityOptions[visibility as keyof typeof visibilityOptions]?.icon ?? TbLayoutGrid;

    const trigger = children ?? (
        <DropdownButton disabled={disabled} size='small' type='dashed'>
            <Row align='middle' wrap={false} style={{ width: '100%' }}>
                <Col flex='none'>
                    <VisibilityIcon style={{ marginRight: '0.25rem', verticalAlign: '-0.125rem' }} />
                    {visibility}
                </Col>
                <Col flex='auto' />
            </Row>
        </DropdownButton>
    );

    return (
        <Dropdown
            disabled={disabled}
            trigger={['click']}
            placement='bottomLeft'
            popupRender={() => (
                <DropdownMenuPanel
                    style={{ marginTop: '0.5rem', width: 16 * 20 }}
                    selectedKeys={[visibility]}
                    onClick={(e) => {
                        setVisibility(e?.key);
                    }}
                >
                    <div>
                        <small style={{ marginLeft: '0.75rem', color: token.colorTextTertiary }}>Visibility</small>
                    </div>
                    <Menu.Divider />
                    {options.map((option) => {
                        const optionConfig = visibilityOptions[option as keyof typeof visibilityOptions];
                        const OptionIcon = optionConfig?.icon ?? TbLayoutGrid;

                        return (
                            <Menu.Item key={option}>
                                <OptionIcon
                                    size='1rem'
                                    style={{ verticalAlign: '-0.175rem', marginRight: '0.25rem' }}
                                />
                                {option}
                                {optionConfig?.description ? (
                                    <>
                                        <br />
                                        <small style={{ color: token.colorTextTertiary }}>
                                            {optionConfig.description}
                                        </small>
                                    </>
                                ) : null}
                            </Menu.Item>
                        );
                    })}
                </DropdownMenuPanel>
            )}
        >
            <span>{trigger}</span>
        </Dropdown>
    );
};

export default ResourceVisibilityDropdown;
